/**
 * v-resizable-columns
 *
 * Reusable Vue directive that makes the columns of a (BootstrapVue) <b-table>
 * resizable by dragging their right border. When the cursor hovers close to a
 * column border it turns into the native "col-resize" cursor.
 *
 * Usage:
 *   <b-table v-resizable-columns ... />
 *   <b-table v-resizable-columns="{ fields: visibleFields, storageKey: 'orders' }" ... />
 *
 * Options (all optional):
 *   fields     {Array}   - the (visible) field definitions in table order; used to
 *                          key persisted widths by field `key` so they survive
 *                          re-ordering and show/hide of columns.
 *   storageKey {string}  - when set, column widths are persisted in localStorage
 *                          under 'columnWidths<storageKey>'.
 *   skipSticky {boolean} - do not make sticky columns (.sticky-col) resizable
 *                          (default: true, because their fixed offsets would break)
 *   flexLast   {boolean} - leave the last resizable column flexible so it absorbs
 *                          slack and keeps the table full-width (default: true).
 *                          Set false when there is no right sticky column, so
 *                          resizing one column never resizes another.
 *   minWidth   {number}  - minimum column width in px (default: 40)
 *   edgeZone   {number}  - px distance from the right border that activates the
 *                          resize handle (default: 6)
 */

const DEFAULT_OPTIONS = {
  fields: null,
  storageKey: null,
  skipSticky: true,
  flexLast: true,
  minWidth: 40,
  edgeZone: 6
};

// Dispatch document.dispatchEvent(new CustomEvent(RESET_EVENT, { detail: storageKey }))
// to reset the widths of the table bound with that storageKey.
export const RESET_EVENT = 'resizable-columns:reset';

function resolveTable(el) {
  return el.tagName === 'TABLE' ? el : el.querySelector('table');
}

function isResizable(th, options) {
  if (!th) return false;
  if (th.classList.contains('no-resize')) return false;
  if (options.skipSticky && th.classList.contains('sticky-col')) return false;
  return true;
}

function applyWidth(th, width) {
  const px = width + 'px';
  th.style.width = px;
  th.style.minWidth = px;
  th.style.maxWidth = px;
}

// True when the element's content is clipped (by an ellipsis or line clamp). The
// +1 tolerance absorbs sub-pixel rounding so non-clipped text is never flagged.
function isOverflowing(el) {
  return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
}

// Min-width declared on the field via `thStyle` (string or object). This is the
// source of truth because BootstrapVue renders thStyle as an *inline* style that
// applyWidth/freezeColumns later overwrite, so it cannot be read back from the DOM.
function fieldMinWidth(field) {
  const ts = field && field.thStyle;
  if (!ts) return 0;
  if (typeof ts === 'string') {
    const m = /min-width\s*:\s*([\d.]+)\s*px/i.exec(ts);
    return m ? parseFloat(m[1]) : 0;
  }
  if (typeof ts === 'object') {
    const m = /([\d.]+)/.exec(String(ts.minWidth || ts['min-width'] || ''));
    return m ? parseFloat(m[1]) : 0;
  }
  return 0;
}

// Initial width declared on the field via `thStyle` (string or object). Used to
// restore the original per-column widths on reset, mirroring how BootstrapVue
// renders thStyle as an inline width on first render.
function fieldWidth(field) {
  const ts = field && field.thStyle;
  if (!ts) return 0;
  if (typeof ts === 'string') {
    const m = /(?<!min-|max-)width\s*:\s*([\d.]+)\s*px/i.exec(ts);
    return m ? parseFloat(m[1]) : 0;
  }
  if (typeof ts === 'object') {
    const m = /([\d.]+)/.exec(String(ts.width || ''));
    return m ? parseFloat(m[1]) : 0;
  }
  return 0;
}

// Min-width coming from a CSS class (not from the inline thStyle). Inline width
// constraints are stripped while measuring, then restored, because
// applyWidth/freezeColumns overwrite minWidth with the current px width.
function classMinWidth(th) {
  const prevWidth = th.style.width;
  const prevMin = th.style.minWidth;
  const prevMax = th.style.maxWidth;
  th.style.width = '';
  th.style.minWidth = '';
  th.style.maxWidth = '';
  const min = parseFloat(window.getComputedStyle(th).minWidth) || 0;
  th.style.width = prevWidth;
  th.style.minWidth = prevMin;
  th.style.maxWidth = prevMax;
  return min;
}

// The floor a column may be dragged to: the largest of the global default and
// the column's own min-width, whether declared via thStyle or a CSS class.
function columnMinWidth(th, index, options) {
  const field = options.fields && options.fields[index];
  return Math.max(options.minWidth, fieldMinWidth(field), classMinWidth(th));
}

// Resolve a stable identity for a column: prefer the field `key` (survives
// re-ordering / show-hide), fall back to the DOM aria-colindex.
function columnKeyFor(th, index, options) {
  if (options.fields && options.fields[index] && options.fields[index].key != null) {
    return options.fields[index].key;
  }
  return th.getAttribute('aria-colindex');
}

function storageId(options) {
  return options.storageKey ? 'columnWidths' + options.storageKey : null;
}

function loadWidths(options) {
  const id = storageId(options);
  if (!id) return {};
  try {
    return JSON.parse(localStorage.getItem(id)) || {};
  } catch (e) {
    return {};
  }
}

function saveWidths(state) {
  const id = storageId(state.options);
  if (!id) return;
  localStorage.setItem(id, JSON.stringify(state.widths));
}

function applyStoredWidths(state) {
  const table = resolveTable(state.el);
  if (!table) return;
  const thead = table.querySelector('thead');
  if (!thead) return;
  thead.querySelectorAll('tr > th').forEach((th, index) => {
    if (!isResizable(th, state.options)) return;
    const key = columnKeyFor(th, index, state.options);
    if (key != null && state.widths[key] != null) applyWidth(th, state.widths[key]);
  });
}

// Tag each header cell that is actually resizable, so the UI (e.g. the resize
// grip affordance) can style exactly those columns via CSS.
function markResizable(state) {
  const table = resolveTable(state.el);
  if (!table) return;
  const thead = table.querySelector('thead');
  if (!thead) return;
  thead.querySelectorAll('tr > th').forEach(th => {
    th.classList.toggle('resizable-col', isResizable(th, state.options));
  });
}

// Pin every resizable column to its current rendered width and switch the table
// to a fixed layout, so dragging one column no longer redistributes the others.
// When flexLast is on, the last non-sticky column is left flexible so it absorbs
// any slack, keeping the table full-width and the right sticky column pinned to
// the right edge. When flexLast is off, every column is pinned and the table
// width follows the sum of the columns, so resizing one never resizes another.
function freezeColumns(state) {
  const table = resolveTable(state.el);
  if (!table) return;
  const thead = table.querySelector('thead');
  if (!thead) return;
  const allTh = Array.prototype.slice.call(thead.querySelectorAll('tr > th'));
  const resizable = allTh.filter(th => isResizable(th, state.options));
  const flexTh = state.options.flexLast ? resizable[resizable.length - 1] : null;
  if (flexTh) {
    resizable.forEach(th => {
      if (th === flexTh) {
        // Stay flexible so it absorbs slack, but never shrink below its own
        // min-width (otherwise widening another column collapses this one).
        th.style.width = '';
        th.style.minWidth = columnMinWidth(th, allTh.indexOf(th), state.options) + 'px';
        th.style.maxWidth = 'none'; // override the .max-width:0 ellipsis rule so it can grow
        return;
      }
      if (!th.style.width) applyWidth(th, th.getBoundingClientRect().width);
    });
    table.style.tableLayout = 'fixed';
    table.style.width = '100%';
  } else {
    // Independent columns: pin EVERY column (including sticky) to a definite width
    // and set the table width to their sum. Using applyWidth on all columns makes
    // the fixed layout DETERMINISTIC — the total is read back from inline widths,
    // never re-measured under fixed layout (which would feed back and inflate the
    // table), and it sets an inline max-width that overrides any leaked
    // `max-width:0` rule that would otherwise collapse a thStyle-width column.
    //
    // Columns without an explicit thStyle width must be measured at their natural
    // (preferred) width. The table's default `width:100%` auto layout shrinks
    // every column to fit the container when the columns overflow, so measuring
    // in that state yields squished widths whose sum never exceeds the viewport —
    // the frozen table then shows no scrollbar and ignores the initial widths.
    // Expanding the table to max-content first lets each column report the width
    // it wants. While doing so the leaked `.table-wrapper (th|td):not(.sticky-col)
    // { max-width:0 }` ellipsis rule must be suspended, otherwise it wins under
    // max-content sizing and collapses those columns to a single character. The
    // probe stylesheet lives in the DOM only for the synchronous measurement (no
    // paint happens in between) and each measured width is capped so one very long
    // cell cannot produce an absurdly wide column. Columns that already carry an
    // inline width use it verbatim, so this never changes explicitly sized tables.
    const MAX_MEASURED_WIDTH = 300;
    const probe = document.createElement('style');
    probe.textContent = '.table-wrapper th, .table-wrapper td { max-width: none !important; }';
    document.head.appendChild(probe);
    table.style.tableLayout = 'auto';
    table.style.width = 'max-content';
    const intended = allTh.map(th =>
      th.style.width
        ? parseFloat(th.style.width)
        : Math.min(th.getBoundingClientRect().width, MAX_MEASURED_WIDTH)
    );
    document.head.removeChild(probe);
    let total = 0;
    allTh.forEach((th, i) => {
      applyWidth(th, intended[i]);
      total += intended[i];
    });
    state.baseTableWidth = total;
    table.style.tableLayout = 'fixed';
    table.style.width = total + 'px';
  }
}

// True once the table has rendered real data rows (not the busy or empty slot).
function hasDataRows(table) {
  const tbody = table.querySelector('tbody');
  return !!(tbody && tbody.querySelector('tr:not(.b-table-empty-row):not(.b-table-busy-slot)'));
}

// On (re)render, establish the frozen fixed layout so column widths take effect.
// Flex tables stay in their default full-width auto layout until the user first
// resizes, so they only need this to restore persisted widths. Non-flex
// (independent) tables always need their definite width (sum of columns)
// established so they can scroll and honor per-column initial widths from the
// first render.
//
// The b-table child updates asynchronously, so its rows may not be in the DOM
// yet when this hook runs. Defer to the next frame(s) until the rows exist, then
// freeze once — before any user interaction — so a later pointer-down doesn't
// switch layout and make the table jump.
function restorePersistedLayout(state) {
  const hasStored = state.widths && Object.keys(state.widths).length > 0;
  if (state.options.flexLast && !hasStored) return;
  if (state.restoreRaf != null) cancelAnimationFrame(state.restoreRaf);
  let tries = 0;
  const attempt = () => {
    state.restoreRaf = null;
    if (state.dragging) return; // never re-freeze mid-drag
    const table = resolveTable(state.el);
    if (table && hasDataRows(table)) {
      freezeColumns(state);
      return;
    }
    if (++tries < 30) state.restoreRaf = requestAnimationFrame(attempt);
  };
  state.restoreRaf = requestAnimationFrame(attempt);
}

function createState(el, binding) {
  const options = { ...DEFAULT_OPTIONS, ...(binding.value || {}) };
  const state = {
    el,
    options,
    widths: loadWidths(options), // keyed by column key so widths survive re-renders
    dragging: false,
    dragMoved: false,
    dragTh: null,
    dragKey: null,
    startX: 0,
    startWidth: 0,
    dragMinWidth: 0,
    baseTableWidth: 0,
    restoreRaf: null,
    hoverTh: null,
    rafId: null,
    lastClientX: 0
  };

  // Show the full text as a native tooltip only while hovering a cell whose
  // content is actually clipped, so the title appears exactly when the ellipsis
  // is visible. Delegated on the table (one listener) and recomputed only when
  // entering a new cell, so it adds no measurable cost. Cells with a
  // template-provided title (marked by having a title we did not set) are left
  // untouched.
  state.titleCell = null;
  state.onCellMouseOver = e => {
    const cell = e.target.closest('thead th, tbody td');
    if (cell === state.titleCell) return;
    state.titleCell = cell;
    if (!cell) return;
    const target = cell.querySelector(':scope > div') || cell;
    const managed = target.hasAttribute('data-ov-title');
    if (target.hasAttribute('title') && !managed) return; // respect explicit titles
    const text = target.textContent.trim();
    if (text && isOverflowing(target)) {
      target.setAttribute('title', text);
      target.setAttribute('data-ov-title', '');
    } else if (managed) {
      target.removeAttribute('title');
      target.removeAttribute('data-ov-title');
    }
  };

  // Excel-like auto-fit: size the column so its widest cell (header or any row)
  // is fully visible. The natural width is read by expanding the table to
  // max-content with the ellipsis max-width clamp suspended and measuring only
  // the target column, then the frozen fixed layout is rebuilt from inline
  // widths so nothing else shifts.
  state.autoFitColumn = th => {
    const table = resolveTable(state.el);
    const thead = table && table.querySelector('thead');
    if (!thead) return;
    const allTh = Array.prototype.slice.call(thead.querySelectorAll('tr > th'));
    const index = allTh.indexOf(th);
    const key = columnKeyFor(th, index, state.options);
    const minW = columnMinWidth(th, index, state.options);

    const prevWidth = th.style.width;
    const prevMin = th.style.minWidth;
    const prevMax = th.style.maxWidth;
    th.style.width = '';
    th.style.minWidth = '';
    th.style.maxWidth = '';
    const probe = document.createElement('style');
    probe.textContent = '.table-wrapper th, .table-wrapper td { max-width: none !important; }';
    document.head.appendChild(probe);
    const prevLayout = table.style.tableLayout;
    const prevTableWidth = table.style.width;
    table.style.tableLayout = 'auto';
    table.style.width = 'max-content';
    const measured = th.getBoundingClientRect().width;
    document.head.removeChild(probe);
    table.style.tableLayout = prevLayout;
    table.style.width = prevTableWidth;
    th.style.width = prevWidth;
    th.style.minWidth = prevMin;
    th.style.maxWidth = prevMax;

    const newWidth = Math.max(minW, Math.ceil(measured) + 1);
    applyWidth(th, newWidth);
    freezeColumns(state); // rebuild the definite layout & table width from inline widths
    if (key != null) {
      state.widths[key] = newWidth;
      saveWidths(state);
    }
  };

  state.onDblClick = e => {
    const th = e.target.closest('thead th');
    if (!isResizable(th, state.options)) return;
    const rect = th.getBoundingClientRect();
    if (rect.right - e.clientX > state.options.edgeZone) return;
    e.preventDefault();
    e.stopPropagation();
    state.autoFitColumn(th);
  };

  // Clicks (incl. both clicks of a double-click) inside the grip zone must not
  // reach the header's sort handler.
  state.onClickCapture = e => {
    const th = e.target.closest('thead th');
    if (!isResizable(th, state.options)) return;
    const rect = th.getBoundingClientRect();
    if (rect.right - e.clientX <= state.options.edgeZone) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  state.onPointerMove = e => {
    if (state.dragging) return;
    const th = e.target.closest('thead th');
    if (state.hoverTh && state.hoverTh !== th) {
      state.hoverTh.style.cursor = '';
      state.hoverTh = null;
    }
    if (!isResizable(th, state.options)) return;
    const rect = th.getBoundingClientRect();
    if (rect.right - e.clientX <= state.options.edgeZone) {
      th.style.cursor = 'col-resize';
      state.hoverTh = th;
    } else {
      th.style.cursor = '';
    }
  };

  state.onPointerDown = e => {
    const th = e.target.closest('thead th');
    if (!isResizable(th, state.options)) return;
    const rect = th.getBoundingClientRect();
    if (rect.right - e.clientX > state.options.edgeZone) return;

    e.preventDefault();
    e.stopPropagation();
    state.dragging = true;
    state.dragMoved = false;
    state.dragTh = th;
    const cells = Array.prototype.slice.call(th.parentNode.children);
    state.dragKey = columnKeyFor(th, cells.indexOf(th), state.options);
    state.dragMinWidth = columnMinWidth(th, cells.indexOf(th), state.options);

    freezeColumns(state);

    // Capture the start width AFTER freezing so the drag starts from the frozen
    // (fixed-layout) width, never a stale auto-layout width that would warp.
    state.startX = e.clientX;
    state.startWidth = th.getBoundingClientRect().width;

    document.addEventListener('mousemove', state.onDocMouseMove);
    document.addEventListener('mouseup', state.onDocMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  state.onDocMouseMove = e => {
    if (!state.dragging) return;
    state.dragMoved = true;
    state.lastClientX = e.clientX;
    // Coalesce rapid mousemove events into a single width update per frame.
    if (state.rafId == null) state.rafId = requestAnimationFrame(state.applyDrag);
  };

  state.applyDrag = () => {
    state.rafId = null;
    if (!state.dragging || !state.dragTh) return;
    const delta = state.lastClientX - state.startX;
    const newWidth = Math.max(state.dragMinWidth, state.startWidth + delta);
    applyWidth(state.dragTh, newWidth);
    // Without a flexible last column the table width must follow the columns,
    // otherwise a fixed-layout table would redistribute the change instead.
    if (!state.options.flexLast) {
      const table = resolveTable(state.el);
      if (table) table.style.width = state.baseTableWidth + (newWidth - state.startWidth) + 'px';
    }
  };

  state.onDocMouseUp = () => {
    document.removeEventListener('mousemove', state.onDocMouseMove);
    document.removeEventListener('mouseup', state.onDocMouseUp);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';

    // Flush any width update still pending in a scheduled frame.
    if (state.rafId != null) {
      cancelAnimationFrame(state.rafId);
      state.applyDrag();
    }

    if (state.dragging && state.dragMoved && state.dragTh && state.dragKey != null) {
      state.widths[state.dragKey] = parseFloat(state.dragTh.style.width);
      saveWidths(state);
      // Prevent the click that follows the drag from triggering column sorting.
      el.addEventListener('click', state.suppressNextClick, true);
    }

    state.dragging = false;
    state.dragTh = null;
    state.dragKey = null;
  };

  state.suppressNextClick = e => {
    el.removeEventListener('click', state.suppressNextClick, true);
    e.stopPropagation();
    e.preventDefault();
  };

  // Reset all columns to their original widths and clear persistence, then
  // re-establish the initial computed layout so the table looks exactly as it
  // did on first load.
  state.reset = () => {
    state.widths = {};
    const id = storageId(state.options);
    if (id) localStorage.removeItem(id);
    const table = resolveTable(state.el);
    const thead = table && table.querySelector('thead');
    if (thead) {
      thead.querySelectorAll('tr > th').forEach((th, index) => {
        th.style.minWidth = '';
        th.style.maxWidth = '';
        // Re-seed the per-column initial width from the field thStyle (as
        // BootstrapVue would on first render), so freezeColumns rebuilds the
        // original layout; leave width-less columns to be measured again.
        const w = fieldWidth(state.options.fields && state.options.fields[index]);
        th.style.width = w ? w + 'px' : '';
      });
    }
    if (table) {
      table.style.tableLayout = '';
      table.style.width = '';
    }
    restorePersistedLayout(state);
  };

  state.onResetEvent = e => {
    if (state.options.storageKey && e.detail === state.options.storageKey) state.reset();
  };

  return state;
}

const directive = {
  inserted(el, binding) {
    const table = resolveTable(el);
    if (!table) return;
    const state = createState(el, binding);
    el.__resizableColumns__ = state;
    el.addEventListener('mousemove', state.onPointerMove);
    el.addEventListener('mousedown', state.onPointerDown);
    el.addEventListener('mouseover', state.onCellMouseOver);
    el.addEventListener('dblclick', state.onDblClick);
    el.addEventListener('click', state.onClickCapture, true);
    document.addEventListener(RESET_EVENT, state.onResetEvent);
    applyStoredWidths(state);
    markResizable(state);
    restorePersistedLayout(state);
  },

  componentUpdated(el, binding) {
    const state = el.__resizableColumns__;
    if (!state) return;
    // Keep the field mapping in sync when columns are shown/hidden or re-ordered.
    if (binding.value && binding.value.fields) state.options.fields = binding.value.fields;
    applyStoredWidths(state);
    markResizable(state);
    restorePersistedLayout(state);
  },

  unbind(el) {
    const state = el.__resizableColumns__;
    if (!state) return;
    if (state.rafId != null) cancelAnimationFrame(state.rafId);
    if (state.restoreRaf != null) cancelAnimationFrame(state.restoreRaf);
    el.removeEventListener('mousemove', state.onPointerMove);
    el.removeEventListener('mousedown', state.onPointerDown);
    el.removeEventListener('mouseover', state.onCellMouseOver);
    el.removeEventListener('dblclick', state.onDblClick);
    el.removeEventListener('click', state.onClickCapture, true);
    document.removeEventListener('mousemove', state.onDocMouseMove);
    document.removeEventListener('mouseup', state.onDocMouseUp);
    document.removeEventListener(RESET_EVENT, state.onResetEvent);
    el.removeEventListener('click', state.suppressNextClick, true);
    delete el.__resizableColumns__;
  }
};

export default directive;
